import { Box, Skeleton, Tooltip } from '@mui/material';
import { EVENT_NAMES_BY_SOURCES, EventName, EventSource, toCursor } from '@loyalty/shared-common';
import {
  EventReportAttributes,
  EventReportStatus,
  GetEventReportsFilters,
  SearchReportsRawQuery,
  defaultEventReportStatus,
} from '@loyalty/shared-common/event-processing-api-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IncomingWebhookType } from '@loyalty/shared-common/ingestor-api-client';
import {
  QlfFilterDetails,
  QlfMasterDetail,
  QlfSimpleDetail,
} from '@qualifio/ui-commons/dist/components/qlf-master-detail';
import { Subject } from 'rxjs';
import { colorPalette } from '@qualifio/ui-commons';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons/faCircleXmark';
import { faRepeat } from '@fortawesome/pro-solid-svg-icons/faRepeat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AccountIncomingWebhooksProvider,
  useAccountIncomingWebhooks,
} from '../../libs/account-incoming-webhooks/accountIncomingWebhookContext';
import { EventReportDetail } from '../../libs/eventReports/components/detail/EventReportDetail';
import { EventReportsProvider, useEventReports } from '../../libs/eventReports/EventReportsContext';
import { Page, PageBody, PageHeader } from '../../libs/shared/page';
import { PaginationFooter } from '../../libs/shared/pagination-footer/PaginationFooter';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { QlfPageErrorBoundary } from '../../libs/shared/QlfPageErrorBoundary';
import { TopBar } from '../../libs/shared/layout/TopBar';
import { getFiltersFromUrl, saveFiltersToQuery } from './utils';
import { useAccounts } from '../../libs/accounts/AccountsContext';
import { useApi } from '../../libs/shared/api-client/ApiClientProvider';
import { usePrograms } from '../../libs/loyalty/ProgramsContext';

const iconStyle = {
  width: '24px',
  height: '24px',
  paddingLeft: 1,
  alignSelf: 'center',
};

const statusIcons: Record<string, any> = {
  loading: (
    <Box sx={{ ...iconStyle, color: colorPalette.green.main }}>
      <Skeleton variant="circular" sx={{ ...iconStyle, color: colorPalette.green.main }} />
    </Box>
  ),
  [EventReportStatus.success]: (
    <Box
      component={FontAwesomeIcon}
      icon={faCircleCheck}
      sx={{ ...iconStyle, color: colorPalette.green.main }}
    />
  ),
  [EventReportStatus.error]: (
    <Box
      component={FontAwesomeIcon}
      icon={faCircleXmark}
      sx={{ ...iconStyle, color: colorPalette.red.main }}
    />
  ),
  [EventReportStatus.orphan]: (
    <Box
      component={FontAwesomeIcon}
      icon={faCircleExclamation}
      sx={{ ...iconStyle, color: colorPalette.orange.main }}
    />
  ),
  [EventReportStatus.orphanReprocessed]: (
    <Box
      component={FontAwesomeIcon}
      icon={faCircleExclamation}
      sx={{ ...iconStyle, color: colorPalette.orange.main }}
    />
  ),
  [EventReportStatus.orphanReprocessing]: (
    <Box
      component={FontAwesomeIcon}
      icon={faCircleExclamation}
      sx={{ ...iconStyle, color: colorPalette.orange.main }}
    />
  ),
  [EventReportStatus.denied]: (
    <Box
      component={FontAwesomeIcon}
      icon={faCircleExclamation}
      sx={{ ...iconStyle, color: colorPalette.orange.main }}
    />
  ),
};

export function EventReports() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<GetEventReportsFilters | undefined>(undefined);
  const [identitySearch, setIdentitySearch] = useState<string | undefined>();
  const [clientEventIdSearch, setClientEventIdSearch] = useState<string | undefined>();
  const [profileIdSearch, setProfileIdSearch] = useState<string | undefined>();
  const [currentQuery, setCurrentQuery] = useState<SearchReportsRawQuery | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(0);
  const { accountWebhooks } = useAccountIncomingWebhooks();
  const { selectedReport, setSelectedReport } = useEventReports();
  const { currentAccount } = useAccounts();
  const { eventProcessingApi } = useApi();

  const eventNamesBySource = useMemo(() => {
    const tempEventNamesBySource: { [key: string]: Set<{ label: string; value: string }> } = {};
    Object.entries(EVENT_NAMES_BY_SOURCES).forEach(([key, value]) => {
      tempEventNamesBySource[key] = new Set(value.map(val => ({ label: val, value: val })));
    });
    return tempEventNamesBySource;
  }, []);

  const actions = useMemo(() => {
    eventNamesBySource[EventSource.BINARY] = new Set([
      { label: EventName.BASIC, value: EventName.BASIC },
    ]);

    accountWebhooks
      .filter(webhook => webhook.type === IncomingWebhookType.MAPPED_EVENT)
      .filter(
        webhook => !filters?.programIds?.length || filters.programIds.includes(webhook.programId),
      )
      .forEach(binaryWebhook =>
        eventNamesBySource[EventSource.BINARY].add({
          label: binaryWebhook.name,
          value: `webhook_${binaryWebhook.id}`,
        }),
      );
    const sources = filters?.sources?.length ? filters.sources : [];

    const actions: { label: string; value: string }[] = [];
    sources.forEach(eventSource => {
      actions.push(...eventNamesBySource[eventSource].values());
    });
    return actions;
  }, [accountWebhooks, filters]);

  const getEventReportEventName = useCallback(
    (eventName?: string) => {
      let name: string | undefined;
      if (eventName?.startsWith('webhook_')) {
        name = accountWebhooks.find(webhook => webhook.id === eventName.slice(8))?.name;
      }
      return name ?? eventName;
    },
    [accountWebhooks],
  );

  const { programs } = usePrograms();
  const { current: identitySearch$ } = useRef(new Subject<string>());
  const { current: clientEventIdSearch$ } = useRef(new Subject<string>());
  const { current: profileIdSearch$ } = useRef(new Subject<string>());

  const { data: reportsData, isPending: isQueryLoading } = useQuery({
    queryKey: ['eventReports', currentAccount.id, currentQuery?.cursor],

    queryFn: () => {
      if (!currentQuery) {
        return undefined;
      }
      return eventProcessingApi.getEventReports(currentAccount.id, currentQuery);
    },

    enabled: !!currentQuery,
  });

  const handleStatusesFilter = (statuses: (string | number)[]) => {
    setFilters({ ...filters, statuses });
  };

  const handleProgramsFilter = (programIds: string[]) => {
    setFilters(prev => ({ ...prev, programIds }));
  };

  const handleSourcesFilter = (sources: EventSource[]) => {
    setFilters(prev => ({
      ...prev,
      sources,
      ...(sources.length ? {} : { eventNames: [] }),
    }));
  };

  const handleEventNamesFilter = (eventNames: EventName[]) => {
    setFilters(prev => ({ ...prev, eventNames }));
  };

  const handleStartDateFilter = (startDate: Date | null) => {
    // apply filter only for valid date
    if (startDate === null) {
      setFilters(prev => ({ ...prev, startDate: undefined }));
      return;
    }
    if (!Number.isNaN(startDate.getTime())) {
      setFilters(prev => ({ ...prev, startDate }));
    }
  };

  const handleEndDateFilter = (endDate: Date | null) => {
    // apply filter only for valid date
    if (endDate === null) {
      setFilters(prev => ({ ...prev, endDate: undefined }));
      return;
    }
    if (!Number.isNaN(endDate.getTime())) {
      setFilters(prev => ({ ...prev, endDate }));
    }
  };

  const handleIdentityFilter = (identity: string) => {
    setFilters(prev => ({ ...prev, identity: identity || undefined }));
  };

  const handleClientEventIdFilter = (clientEventId: string) => {
    setFilters(prev => ({ ...prev, clientEventId: clientEventId || undefined }));
  };

  const handleProfileIdFilter = (profileId: string) => {
    setFilters(prev => ({ ...prev, profileId: profileId || undefined }));
  };

  const handleIsTestUserFilter = (isTestUser: boolean) => {
    setFilters(prev => ({ ...prev, isTestUser }));
  };
  const handleIsRepublishedFilter = (isRepublished: boolean) => {
    setFilters(prev => ({ ...prev, isRepublished }));
  };

  const templateHeader = () => (
    <QlfFilterDetails
      placeholder={t('event-reports.sidebar.search')}
      value={identitySearch}
      onChange={e => {
        setIdentitySearch(e.target.value);
        identitySearch$.next(e.target.value);
      }}
      addButton={false}
      withFilters={{
        filters: [
          {
            type: 'textField',
            name: 'clientEventId',
            label: t('event.report.details.properties.clientEventId'),
            placeholder: t('master-detail.event-reports.filters.clientEventId.placeholder'),
            value: clientEventIdSearch ?? '',
            inputType: 'string',
            onChange: value => {
              const nextValue = value ?? '';
              setClientEventIdSearch(nextValue);
              clientEventIdSearch$.next(nextValue);
            },
          },
          {
            type: 'textField',
            name: 'profileId',
            label: t('event.report.profile.properties.profile.id'),
            placeholder: t('master-detail.event-reports.filters.profileId.placeholder'),
            value: profileIdSearch ?? '',
            inputType: 'string',
            onChange: value => {
              const nextValue = value ?? '';
              setProfileIdSearch(nextValue);
              profileIdSearch$.next(nextValue);
            },
          },
          {
            name: t('event.report.details.properties.status'),
            type: 'dropDown',
            selectedOptions: filters?.statuses || [],
            // TODO modify QlfSearchBar.tsx function onchange
            // signature should be (string[] | number[]) => void instead of (string|number)[] => void
            onChange: handleStatusesFilter,
            options: defaultEventReportStatus.map(status => ({
              value: status,
              label: t(`master-detail.event-reports.filters.${status}`),
            })),
          },
          {
            type: 'checkBox',
            name: t('master-detail.event-reports.filters.test-user'),
            checked: !!filters?.isTestUser,
            onChange: handleIsTestUserFilter as any,
          },
          {
            type: 'checkBox',
            name: t('master-detail.event-reports.filters.republished'),
            checked: !!filters?.isRepublished,
            onChange: handleIsRepublishedFilter as any,
          },
          {
            type: 'dropDown',
            name: t('event-report.select.program.helper'),
            selectedOptions: filters?.programIds || [],
            onChange: handleProgramsFilter as any,
            options: programs?.map(program => ({
              value: program.id,
              label: program.name,
            })),
          },
          {
            type: 'dropDown',
            name: t('event-report.select.source.helper'),
            selectedOptions: filters?.sources || [],
            onChange: handleSourcesFilter as any,
            options: Object.keys(EventSource).map(source => ({
              value: source,
              label: source,
            })),
          },
          ...(filters?.sources?.length
            ? [
                {
                  type: 'dropDown' as const,
                  name: t('event-report.select.eventName.helper'),
                  selectedOptions: filters?.eventNames || [],
                  onChange: handleEventNamesFilter as any,
                  options: actions,
                },
              ]
            : []),
          {
            type: 'datePicker',
            name: t('event-report.select.starting-date.helper'),
            value: filters?.startDate || null,
            onChange: handleStartDateFilter as any,
          },
          {
            type: 'datePicker',
            name: t('event-report.select.ending-date.helper'),
            value: filters?.endDate || null,
            onChange: handleEndDateFilter as any,
          },
        ],
      }}
    />
  );

  const templateRow = (props: { data: EventReportAttributes }) => {
    const { data: report } = props;

    if (!report || isQueryLoading) {
      return (
        <QlfSimpleDetail
          state={'disabled'}
          content={<Skeleton variant="text" sx={{ width: '80px', marginLeft: '10px' }} />}
          callback={() => {
            console.log('loading');
          }}
          secondaryText={
            <Skeleton
              variant="rectangular"
              sx={{
                marginRight: '10px',
                width: '100px',
                height: '22px',
                borderRadius: '3px',
                marginLeft: '10px',
              }}
            />
          }
          image={
            <Box sx={iconStyle}>
              <Skeleton variant="circular" sx={iconStyle} />
            </Box>
          }
        />
      );
    }

    return (
      <QlfSimpleDetail
        state={report?.id === selectedReport?.id ? 'active' : 'default'}
        callback={() => setSelectedReport(props.data)}
        content={report.eventSource}
        secondaryText={
          <div
            style={{
              fontWeight: 400,
              fontSize: '12px',
              color: 'grey',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              margin: 0,
              marginTop: '2px',
            }}
          >
            <>
              {getEventReportEventName(report.eventName)}
              {(report.isRepublished || report.status === EventReportStatus.orphanReprocessed) && (
                <Tooltip title={t('event.report.details.properties.republished')} placement="top">
                  <Box
                    component={FontAwesomeIcon}
                    icon={faRepeat}
                    sx={{
                      marginLeft: 0.5,
                      fontSize: '10px',
                      verticalAlign: 'middle',
                      color: 'grey',
                    }}
                  />
                </Tooltip>
              )}
              <br />
              {report.createdAt}
            </>
          </div>
        }
        image={statusIcons[report?.status]}
      />
    );
  };

  const changePage = (pageNumber: number) => {
    if (!isQueryLoading && reportsData) {
      if (pageNumber > 0 && reportsData.next) {
        setCurrentPage(prev => prev + 1);
        setCurrentQuery({ cursor: reportsData.next });
      } else if (pageNumber < 0 && reportsData.previous) {
        setCurrentPage(prev => prev - 1);
        setCurrentQuery({ cursor: reportsData.previous });
      }
    }
  };

  const templateFooter = (
    <PaginationFooter
      changePage={changePage}
      currentPage={currentPage}
      pageSize={filters?.limit}
      filters={filters}
      dataPage={reportsData}
      isLoading={isQueryLoading}
      getCount={() =>
        eventProcessingApi.countEventReports(currentAccount.id, {
          cursor: toCursor({ filter: filters }),
        })
      }
    />
  );

  // Update filter based on identity search input
  useEffect(() => {
    const subscription = identitySearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(handleIdentityFilter);
    const clientEventIdSubscription = clientEventIdSearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(handleClientEventIdFilter);
    const profileIdSubscription = profileIdSearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(handleProfileIdFilter);

    return () => {
      subscription.unsubscribe();
      clientEventIdSubscription.unsubscribe();
      profileIdSubscription.unsubscribe();
    };
  }, [identitySearch$, clientEventIdSearch$, profileIdSearch$]);

  // Update query (in url and react-query request) when filters change
  useEffect(() => {
    if (filters) {
      setCurrentPage(0);
      setCurrentQuery({ cursor: toCursor({ filter: filters }) });
      saveFiltersToQuery(filters);
    }
  }, [filters]);

  // Set initial filters from url
  useEffect(() => {
    const queryFilters = getFiltersFromUrl();
    setFilters(queryFilters);
    setIdentitySearch(queryFilters?.identity);
    setClientEventIdSearch(queryFilters?.clientEventId);
    setProfileIdSearch(queryFilters?.profileId);
  }, []);

  return (
    <Page>
      <PageHeader>
        <TopBar />
      </PageHeader>
      <PageBody>
        <QlfPageErrorBoundary>
          <QlfMasterDetailContainer isAccountLevel>
            <QlfMasterDetail
              templateHeader={templateHeader}
              templateRow={templateRow}
              templateDetail={EventReportDetail}
              templateFooter={templateFooter}
              items={
                isQueryLoading
                  ? // for skelletons
                    ([
                      null,
                      null,
                      null,
                      null,
                      null,
                      null,
                      null,
                      null,
                      null,
                      null,
                    ] as any as EventReportAttributes[])
                  : reportsData?.data || []
              }
            />
          </QlfMasterDetailContainer>
        </QlfPageErrorBoundary>
      </PageBody>
    </Page>
  );
}

export function EventReportsIndexPage() {
  return (
    <AccountIncomingWebhooksProvider>
      <EventReportsProvider>
        <EventReports />
      </EventReportsProvider>
    </AccountIncomingWebhooksProvider>
  );
}
